'use client'

import { useEffect } from 'react'

/** Máx. de reports por pageview — trava final anti-flood. */
const MAX_PER_PAGEVIEW = 5
/** Dedupe PERSISTENTE por URL: não reportar a mesma imagem 2× no mesmo navegador dentro desta janela. */
const DEDUPE_TTL_MS = 24 * 60 * 60 * 1000 // 24h
const STORE_KEY = 'pc_broken_img_v1'
/** Teto de entradas no store persistente (poda as mais antigas). */
const STORE_MAX = 200

/**
 * Hosts PRÓPRIOS. Falhas nos NOSSOS assets (logo, banners, mídia self-host em `media-beta`) NÃO vão
 * por este beacon: sob carga a origem serve o `media-beta` pelo mesmo túnel, então uma lentidão faz a
 * `<img>` estourar timeout em TODO visitante → onerror → POST → mais carga → laço de auto-amplificação
 * (incidente 2026-07-07: logo + banner geraram ~98% de 108k hits). O beacon existe para pegar
 * CDNs/hotlinks EXTERNOS que só o browser vê; nossos próprios assets são monitorados server-side.
 */
function isOwnHost(host: string): boolean {
  return /(^|\.)balizo\.com\.br$/i.test(host)
}

type Store = Record<string, number> // imageUrl -> lastReportedAt (ms)

function loadStore(): Store {
  try {
    const raw = localStorage.getItem(STORE_KEY)
    return raw ? (JSON.parse(raw) as Store) : {}
  } catch {
    return {}
  }
}

function saveStore(store: Store): void {
  try {
    let entries = Object.entries(store)
    if (entries.length > STORE_MAX) {
      // mantém as STORE_MAX mais recentes
      entries = entries.sort((a, b) => b[1] - a[1]).slice(0, STORE_MAX)
    }
    localStorage.setItem(STORE_KEY, JSON.stringify(Object.fromEntries(entries)))
  } catch {
    /* localStorage indisponível (modo privado/quota) — dedupe persistente é best-effort */
  }
}

/**
 * Reporta imagens quebradas para `/api/public/broken-image-hit`.
 * Eventos `error` de `<img>` NÃO fazem bubble — listener em fase de captura no `document`.
 * Cobre também imagens que falharam antes da hidratação (varredura inicial de `naturalWidth === 0`).
 * Só o browser vê falhas de mídia em CDN externo / hotlinks — por isso a detecção é client-side.
 *
 * Guardas anti-flood (incidente 2026-07-07):
 *  - ignora nossos próprios hosts (`isOwnHost`) — evita auto-amplificação sob carga;
 *  - dedupe PERSISTENTE por URL em localStorage (1× por navegador por 24h), além do dedupe por pageview;
 *  - cap `MAX_PER_PAGEVIEW`.
 */
export function BrokenImageReporter(): null {
  useEffect(() => {
    const now = Date.now()
    const store = loadStore()
    // Expira entradas antigas para o store não crescer indefinidamente.
    for (const [url, ts] of Object.entries(store)) {
      if (typeof ts !== 'number' || now - ts > DEDUPE_TTL_MS) delete store[url]
    }

    const reportedThisView = new Set<string>()
    let sent = 0

    const report = (src: string) => {
      if (!src || sent >= MAX_PER_PAGEVIEW) return
      if (src.startsWith('data:') || src.startsWith('blob:')) return
      // Só http(s) é registável no servidor — ignora `chrome-extension://`, `about:`, etc.
      if (!/^https?:\/\//i.test(src)) return
      if (reportedThisView.has(src)) return

      let host = ''
      try {
        host = new URL(src).host
      } catch {
        return
      }
      // Não reportar nossos próprios assets (auto-amplificação sob carga — ver isOwnHost).
      if (isOwnHost(host)) return

      // Dedupe persistente: já reportado por este navegador nas últimas 24h?
      const last = store[src]
      if (typeof last === 'number' && now - last < DEDUPE_TTL_MS) {
        reportedThisView.add(src)
        return
      }

      reportedThisView.add(src)
      store[src] = now
      sent += 1
      void fetch('/api/public/broken-image-hit', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ imageUrl: src, pagePath: window.location.pathname || '/' }),
        keepalive: true,
      }).catch(() => {})
      saveStore(store)
    }

    const onError = (e: Event) => {
      const t = e.target
      if (t instanceof HTMLImageElement && t.src) {
        report(t.src)
      }
    }

    document.addEventListener('error', onError, true)

    // Imagens que já falharam antes deste efeito correr (SSR + erro pré-hidratação).
    for (const img of Array.from(document.images)) {
      if (img.src && img.complete && img.naturalWidth === 0) {
        report(img.src)
      }
    }

    return () => document.removeEventListener('error', onError, true)
  }, [])

  return null
}
